import { jsx as _jsx } from "react/jsx-runtime";
import { useLocation, useNavigate } from 'react-router-dom';
import { buildDocumentCreateUrl, ParticipantDocumentsView } from 'libs.consent.react';
import { useFeatureFlags } from 'libs.flags.react';
import { Spinner, useLocalizeMessage } from 'libs.nucleus';
import { SITE_FEATURE_FLAGS } from '../../constants/feature_flags.constants';
import { getApiErrorMessage, getApiInstance } from '../../helpers/api.helpers';
import { appendSiteIdToPath } from '../../helpers/links.helpers';
import { getParticipantNumber, isParticipantDeactivated, isParticipantInactive, } from '../../helpers/participants.helpers';
import { useAxonAccountContext } from '../../hooks/use_axon_account_context';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useLumosAuth } from '../../hooks/use_lumos_auth';
import { useOrgContext } from '../../hooks/use_org_context';
import { useSiteContext } from '../../hooks/use_site_context';
import { useStudyEConsentEnabled } from '../../hooks/use_study_econsent_enabled';
import { useTranslateDisplayName } from '../../hooks/use_translate_display_name';
import { toDocumentOriginState } from '../../pages/documents/document_origin.helpers';
import { LUMOS_PERMISSIONS } from '../../types/permissions.types';
export const ParticipantDocuments = ({ participant }) => {
    const navigate = useNavigate();
    const { pathname, search, hash } = useLocation();
    const translate = useLocalizeMessage();
    const { environment } = useCurrentEnvironment();
    const site = useSiteContext();
    const org = useOrgContext();
    const account = useAxonAccountContext();
    const formatCaregiverDisplayName = useTranslateDisplayName();
    const { isUserPermitted, isSiteReviewer, canDownloadConsentPdf, requiresIcfAccessWarning } = useLumosAuth();
    const { isFeatureEnabled } = useFeatureFlags();
    const { isEConsentEnabled, isLoading: isStudyEConsentLoading } = useStudyEConsentEnabled();
    if (!environment || !site?._id || !account?._id || isStudyEConsentLoading) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    const isDocGenEconsentEnabled = isFeatureEnabled(SITE_FEATURE_FLAGS.DOC_GEN_ECONSENT) && isEConsentEnabled;
    const canCreateDocument = !!participant &&
        !isParticipantDeactivated(participant) &&
        !isParticipantInactive(participant) &&
        isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_CREATE_CONSENTS);
    const canVoidConsents = isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_VOID_CONSENT);
    const accountDisplayName = `${account.name.first} ${account.name.last}`.trim();
    const getDocumentDetailsPath = (documentId) => `/site/study/${environment.id}/documents/${documentId}`;
    // The hash keeps the participant on the Consent tab when the details page sends them back.
    const openDocumentDetails = (documentId) => navigate(getDocumentDetailsPath(documentId), {
        state: toDocumentOriginState({
            path: `${pathname}${search}${hash}`,
            label: [participant && getParticipantNumber(participant), translate('generic.consent', { count: 1 })]
                .filter(Boolean)
                .join(' — '),
        }),
    });
    return (_jsx(ParticipantDocumentsView, { participant: participant, accountId: account._id, siteId: site._id, apiClient: getApiInstance(environment), environment: environment, getApiInstance: getApiInstance, getApiErrorMessage: getApiErrorMessage, isDocGenEconsentEnabled: isDocGenEconsentEnabled, isNoPiiSite: org?.c_no_pii === true || site?.c_no_pii === true, isCaregiverEnabled: org?.c_caregiver_enabled ?? false, canCreateDocument: canCreateDocument, canVoidConsents: canVoidConsents, 
        // Classic title hrefs go to Site App, which site reviewers cannot access.
        // DocGen on: primary click opens the Studio modal instead of following the href.
        canOpenDocument: isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_VIEW_CONSENT_PDF) && (!isSiteReviewer || isDocGenEconsentEnabled), canDownloadConsentPdf: canDownloadConsentPdf, requiresIcfAccessWarning: requiresIcfAccessWarning, formatCaregiverDisplayName: formatCaregiverDisplayName, openClassicCreateConsent: () => {
            const createDocumentUrl = buildDocumentCreateUrl(participant?._id, environment);
            if (createDocumentUrl && site._id) {
                window.open(appendSiteIdToPath(createDocumentUrl, site._id), '_blank');
            }
        }, accountEmail: account.email, accountDisplayName: accountDisplayName, 
        // The view ignores these unless DocGen eConsent is on; the details page itself
        // redirects to participants when the flag is off.
        onOpenDocumentDetails: (document) => openDocumentDetails(document._id), getDocumentDetailsHref: (document) => getDocumentDetailsPath(document._id), onDocumentCreated: openDocumentDetails }));
};
